/**
 * Pure helpers for the V2 dependent-exclusion detail view.
 *
 * Kept side-effect-free so they can be unit-tested without mounting
 * the Vue component or touching the Vuex store.
 *
 * Ticket state codes come from CONTEXT.md (Ticket States table):
 *   1 = CREATED                         (legacy V1, cancel-only)
 *   7 = UNDER_REVIEW_BEWELL            (broker review)
 *   8 = UNDER_REVIEW_INSURANCE_COMPANY (insurer review)
 *   3 = EXECUTED                       (terminal)
 *   4 = CANCELED                       (terminal)
 */

import {
  POST_V2_SEND_TO_INSURANCE_DEPENDENTS_EXCLUSION,
  POST_V2_FINISH_DEPENDENTS_EXCLUSION,
  POST_V2_CANCEL_DEPENDENTS_EXCLUSION
} from '../../../../../../store/ticket/dependents-exclusion/individualExclusion/types';

// Short aliases for callers; same string values as the V2 store.
export const POST_V2_SEND_TO_INSURANCE =
  POST_V2_SEND_TO_INSURANCE_DEPENDENTS_EXCLUSION;
export const POST_V2_FINISH = POST_V2_FINISH_DEPENDENTS_EXCLUSION;
export const POST_V2_CANCEL = POST_V2_CANCEL_DEPENDENTS_EXCLUSION;

export const STATE_CREATED = 1;
export const STATE_UNDER_REVIEW_BEWELL = 7;
export const STATE_UNDER_REVIEW_INSURANCE_COMPANY = 8;
export const STATE_EXECUTED = 3;
export const STATE_CANCELED = 4;

const NON_TERMINAL_STATES = new Set([
  STATE_CREATED,
  STATE_UNDER_REVIEW_BEWELL,
  STATE_UNDER_REVIEW_INSURANCE_COMPANY
]);

const TERMINAL_STATES = new Set([STATE_EXECUTED, STATE_CANCELED]);

const READ_ONLY_ROLES = new Set(['rrhh']);

/**
 * Decide which status-action buttons should be visible on the detail
 * view for a given ticket state and viewer role.
 *
 * @param {number|null|undefined} stateId - Ticket state id
 * @param {string} userRole - Viewer role name
 * @returns {{ sendToInsurance: boolean, finish: boolean, cancel: boolean, editEndDate: boolean }}
 */
export const getAvailableActions = (stateId, userRole) => {
  // Coerce to number — the API may return a string ("7" vs 7).
  // Set.has() and switch/case use strict equality (===).
  stateId = Number(stateId);

  const noActions = {
    sendToInsurance: false,
    finish: false,
    cancel: false,
    editEndDate: false
  };

  if (stateId == null || Number.isNaN(stateId)) return { ...noActions };
  if (TERMINAL_STATES.has(stateId)) return { ...noActions };
  if (READ_ONLY_ROLES.has(userRole)) return { ...noActions };
  if (!NON_TERMINAL_STATES.has(stateId)) return { ...noActions };

  switch (stateId) {
    case STATE_CREATED:
      return {
        sendToInsurance: false,
        finish: false,
        cancel: true,
        editEndDate: false
      };
    case STATE_UNDER_REVIEW_BEWELL:
      return {
        sendToInsurance: true,
        finish: false,
        cancel: true,
        editEndDate: false
      };
    case STATE_UNDER_REVIEW_INSURANCE_COMPANY:
      return {
        sendToInsurance: false,
        finish: true,
        cancel: true,
        editEndDate: true
      };
    default:
      return { ...noActions };
  }
};

/**
 * Inspect a V2 status-transition response and tell the caller whether
 * the broker email failed. The exclusion itself may still have been
 * executed — we only want to surface an in-app notification in that
 * case.
 *
 * Accepts both a raw service response (`{ emailSent: false }`) and an
 * axios-style wrapped response (`{ data: { emailSent: false } }`).
 *
 * @param {object|null|undefined} response
 * @returns {boolean}
 */
export const extractEmailFailure = (response) => {
  if (!response || typeof response !== 'object') return false;

  const payload =
    response.data && typeof response.data === 'object'
      ? response.data
      : response;

  if (payload.emailSent === false) return true;
  if (typeof payload.emailError === 'string' && payload.emailError.length > 0) {
    return true;
  }
  return false;
};

const dashIfEmpty = (value) => {
  if (value == null) return '-';
  if (typeof value === 'string' && value.trim() === '') return '-';
  return value;
};

const joinName = (first, last) => {
  const parts = [first, last].filter(
    (p) => typeof p === 'string' && p.trim() !== ''
  );
  return parts.length > 0 ? parts.join(' ') : '-';
};

/**
 * Shape the V2 records payload into display-friendly rows for the
 * detail view's exclusions table.
 *
 * Expected input fields (per the V2 endpoint contract):
 *   - id
 *   - dependent.fullName
 *   - employeeCoverage.coverageName
 *   - employeeCoverage.policyNumber
 *   - employeeCoverage.groupName
 *
 * @param {Array|null|undefined} records
 * @returns {Array<{ id, dependentName, coverageName, policyNumber, groupName }>}
 */
export const mapV2RecordsToDisplay = (records) => {
  if (!Array.isArray(records)) return [];

  return records.map((r) => {
    const dependent = r.dependent || {};
    const employeeCoverage = r.employeeCoverage || {};

    return {
      id: r.id,
      dependentName: dashIfEmpty(dependent.fullName),
      coverageName: dashIfEmpty(employeeCoverage.coverageName),
      policyNumber: dashIfEmpty(employeeCoverage.policyNumber),
      groupName: dashIfEmpty(employeeCoverage.groupName)
    };
  });
};

/**
 * Normalize flat V1 exclusion records into the V2 nested shape so
 * mapV2RecordsToDisplay can render them. The V1 endpoint returns
 * flat fields (firstName, firstSurname) at the top level instead of
 * nesting them under `dependent`. Coverage/policy/group data may
 * not be present in the V1 list endpoint; missing fields display
 * as dashes.
 *
 * @param {Array|null|undefined} records - V1 flat records
 * @returns {Array} V2-shaped records
 */
export const normalizeV1ExclusionRecords = (records) => {
  if (!Array.isArray(records)) return [];
  return records.map((r) => ({
    id: r.id,
    dependent: {
      fullName: joinName(r.firstName, r.firstSurname)
    },
    employeeCoverage: {
      coverageName: r.coverageName || (r.coverage && r.coverage.name),
      policyNumber: r.policyNumber || (r.policy && r.policy.numberPolicy),
      groupName: r.groupName || (r.groupCompany && r.groupCompany.name)
    }
  }));
};

/**
 * Build a bundle of status-action handlers that dispatch V2 store
 * actions and react to results (success / email-fail / error).
 *
 * The handlers are intentionally framework-agnostic: they accept a
 * `notify(type, title, message)` callback so the view can plug in
 * `openNotificationWithIcon` from `@/utils/notifications` (which we
 * don't import here to keep the helper free of UI deps).
 *
 * @param {object} deps
 * @param {{ dispatch: Function }} deps.store - Vuex store
 * @param {number|string} deps.ticketId
 * @param {(type: 'success'|'error'|'warning', title: string, message: string) => void} deps.notify
 * @param {() => Promise<void>} [deps.refresh] - optional callback to re-fetch ticket data after a transition
 * @returns {{ sendToInsurance: Function, finish: Function, cancel: Function }}
 */
export const createStatusActions = (deps) => {
  const { store, ticketId, notify, refresh } = deps;

  const safeRefresh = async () => {
    if (typeof refresh === 'function') {
      await refresh();
    }
  };

  const wrapErrorMessage = (error, fallback) => {
    if (error && error.message) return error.message;
    return fallback;
  };

  const sendToInsurance = async () => {
    try {
      await store.dispatch(POST_V2_SEND_TO_INSURANCE, { ticketId });
      await safeRefresh();
      notify(
        'success',
        'Correcto',
        'Baja de cargas enviada a la aseguradora correctamente.'
      );
    } catch (error) {
      notify(
        'error',
        'Error',
        wrapErrorMessage(
          error,
          'Ocurrió un error al enviar a la aseguradora. Por favor inténtalo más tarde.'
        )
      );
    }
  };

  const finish = async () => {
    let response;
    try {
      response = await store.dispatch(POST_V2_FINISH, { ticketId });
    } catch (error) {
      notify(
        'error',
        'Error',
        wrapErrorMessage(
          error,
          'Ocurrió un error al ejecutar la baja de cargas. Por favor inténtalo más tarde.'
        )
      );
      return;
    }

    await safeRefresh();

    if (extractEmailFailure(response)) {
      notify(
        'warning',
        'Aviso',
        'La baja de cargas fue ejecutada, pero el correo al corredor no pudo ser enviado. Revise la configuración de correo.'
      );
    } else {
      notify('success', 'Correcto', 'Baja de cargas ejecutada correctamente.');
    }
  };

  const cancel = async () => {
    try {
      await store.dispatch(POST_V2_CANCEL, { ticketId });
      await safeRefresh();
      notify('success', 'Correcto', 'Baja de cargas cancelada correctamente.');
    } catch (error) {
      notify(
        'error',
        'Error',
        wrapErrorMessage(
          error,
          'Ocurrió un error al cancelar la baja de cargas. Por favor inténtalo más tarde.'
        )
      );
    }
  };

  return { sendToInsurance, finish, cancel };
};
